import { useEffect, useRef, useState } from 'react';
import mqtt, { MqttClient } from 'mqtt';
import { Play, Square, Radio } from '../../ui/icons';
import Button from '../../ui/Button';
import Colors, { withAlpha } from '../../../constants/Colors';
import { opsHealthApi } from '../../../services/api';

export type MqttConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'closed';

export const MQTT_STATUS_STYLE: Record<MqttConnectionStatus, { label: string; bg: string; fg: string }> = {
  idle: { label: 'Not connected', bg: Colors.background, fg: Colors.textLight },
  connecting: { label: 'Connecting…', bg: withAlpha('#FF9500', 0.14), fg: '#FF9500' },
  connected: { label: 'Live', bg: withAlpha(Colors.success, 0.14), fg: Colors.success },
  error: { label: 'Error', bg: withAlpha(Colors.error, 0.14), fg: Colors.error },
  closed: { label: 'Disconnected', bg: Colors.background, fg: Colors.textLight },
};

/** Shared connect/disconnect lifecycle for a single-topic MQTT-over-WebSocket subscription
 * (AWS IoT Core, custom authorizer) - fetches a fresh token per connect and hands every raw
 * message payload to `onMessage`. Callers own topic construction and payload parsing. */
export function useMqttFeed(topic: string | null, onMessage: (raw: string) => void) {
  const [status, setStatus] = useState<MqttConnectionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<MqttClient | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const disconnect = () => {
    clientRef.current?.end(true);
    clientRef.current = null;
    setStatus('closed');
  };

  useEffect(() => disconnect, []);

  const connect = async () => {
    if (!topic) return;
    disconnect();
    setError(null);
    setStatus('connecting');

    const response = await opsHealthApi.getMqttConnection();
    if (!response.success || !response.data?.success || !response.data.ws_url) {
      setStatus('error');
      setError(response.error || response.data?.error || 'Failed to get an MQTT connection token');
      return;
    }

    const client = mqtt.connect(response.data.ws_url, { reconnectPeriod: 2000 });
    clientRef.current = client;

    client.on('connect', () => {
      setStatus('connected');
      client.subscribe(topic, (err) => {
        if (err) {
          setStatus('error');
          setError(`Failed to subscribe to ${topic}: ${err.message}`);
        }
      });
    });

    client.on('message', (_topic, payload) => onMessageRef.current(payload.toString()));
    client.on('reconnect', () => setStatus('connecting'));
    client.on('close', () => setStatus((prev) => (prev === 'error' ? prev : 'closed')));
    client.on('error', (err) => {
      setStatus('error');
      setError(err.message);
    });
  };

  return { status, error, connect, disconnect };
}

export function MqttStatusBar({
  status,
  topicLabel,
  onConnect,
  onDisconnect,
  connectDisabled,
  connectLoading,
}: {
  status: MqttConnectionStatus;
  topicLabel?: string | null;
  onConnect: () => void;
  onDisconnect: () => void;
  connectDisabled?: boolean;
  connectLoading?: boolean;
}) {
  const statusStyle = MQTT_STATUS_STYLE[status];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px', height: '26px', padding: '0 10px',
          borderRadius: '999px', fontSize: '11px', fontWeight: 700, background: statusStyle.bg, color: statusStyle.fg,
        }}
      >
        <Radio size={12} />
        {statusStyle.label}
      </span>

      {topicLabel && (
        <span style={{ fontSize: '11px', color: Colors.textLight, fontFamily: "'SF Mono', monospace" }}>
          {topicLabel}
        </span>
      )}

      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
        {status === 'connected' || status === 'connecting' ? (
          <Button variant="outline" size="small" onClick={onDisconnect}>
            <Square size={13} />
            Stop
          </Button>
        ) : (
          <Button variant="outline" size="small" onClick={onConnect} disabled={connectDisabled} loading={connectLoading}>
            <Play size={13} />
            Start live feed
          </Button>
        )}
      </div>
    </div>
  );
}
